import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { Dispatch } from 'redux';

import { ErrorEnum, VideoStatus } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { canSeeVideoControls } from 'js/src/data/account';
import { LOCKED_FEATURES, MODAL_IDS, showModal } from 'js/src/data/modals';
import { isVideoRendering, loadRenderDetails, setVideoStatus } from 'js/src/data/share-video';
import { HTTP_STATUS_CODES } from 'js/src/libs/consts';
import { getDownloadLink } from 'js/src/libs/download-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { RootState } from 'js/src/libs/RootState';
import { useAppDispatch } from 'js/src/libs/RootState';
import UserPrintableError from 'js/src/libs/user-printable-error';
import VideoDownloader, {
    DownloadHttpError,
    DownloadLockedError,
} from 'js/src/libs/video-downloader';

/**
 * Based on the videoStatus and other states of the component, this hook will either:
 * 1. start the rendering process if user clicks on Download button (videoStatus is currently DRAFT)
 * 2. autodownload a QUEUED|PROCESSING video if the user came from Publish button
 * 3. populate downloadUrl if user loads this share page with a PUBLISHED video
 *
 * It also re-uses error handling logic from VideoDownloader to handle download errors (re-used from dashboard and creator)
 *
 * @param videoDownloader - VideoDownloader: we use it to fetch the download url from GCS
 * @param videoDownloadUrl - GCS download url, could be null based on component state
 * @param downloadTag - the button element used to trigger autodownload
 * @param downloadDisabled - reduced boolean of various conditions that prevents user from downloading (permissions, failed render...)
 */
export const useDownloadVideoByVideoStatus = (
    videoDownloader: VideoDownloader | null,
    videoDownloadUrl: string | null,
    downloadTag: HTMLButtonElement | HTMLAnchorElement | null,
    downloadDisabled: boolean,
) => {
    const videoStatus = useSelector((state: RootState) => state.shareVideo.video?.status);
    const autoDownload = useSelector((state: RootState) => state.shareVideo.autoDownload);
    const canDownload = useSelector(canSeeVideoControls);

    useEffect(() => {
        if (!canDownload) {
            return;
        }
        if (!videoDownloader) {
            return;
        }

        if (videoStatus !== VideoStatus.PUBLISHED) {
            return;
        }

        if (!videoDownloadUrl) {
            // fetch the download url if we don't already have it.
            videoDownloader.download();
        } else {
            // autodownload once video is rendered
            if (autoDownload && downloadTag && !downloadDisabled) {
                downloadTag.click();
            }
        }
    }, [
        autoDownload,
        canDownload,
        downloadDisabled,
        downloadTag,
        videoDownloadUrl,
        videoDownloader,
        videoStatus,
    ]);
};

/**
 * Listens to video status in order to poll for render status
 * meant to be used in conjunction with useDownloadVideoByVideoStatus() hook, and loadVideo() initially
 * the actual ajax call is separate from state logic to avoid immediate infinite loops
 */
const POLL_INTERVAL_DEFAULT = 5000; // 5s for short render estimates
const POLL_INTERVAL_LONG_QUEUE = 30000; // 30s when estimated time > 10 minutes
const LONG_QUEUE_THRESHOLD_MINUTES = 10;

export const useLoadRenderDetailsAndPoll = () => {
    const _isVideoRendering = useSelector(isVideoRendering);
    const videoStatus = useSelector((state: RootState) => state.shareVideo.video?.status);
    const estimatedTimeRemaining = useSelector(
        (state: RootState) => state.shareVideo.renderEstimate?.estimated_time_remaining ?? 0,
    );
    const dispatch = useAppDispatch();
    const [shouldPoll, setShouldPoll] = useState(false);

    const pollInterval =
        estimatedTimeRemaining > LONG_QUEUE_THRESHOLD_MINUTES
            ? POLL_INTERVAL_LONG_QUEUE
            : POLL_INTERVAL_DEFAULT;

    useEffect(() => {
        if (shouldPoll) {
            const timeoutId = setTimeout(() => {
                dispatch(loadRenderDetails());
                setShouldPoll(false);
            }, pollInterval);
            return () => clearTimeout(timeoutId);
        }
    }, [dispatch, shouldPoll, pollInterval]);

    useEffect(() => {
        if (!SERVER.USER) {
            // no need to poll if user is not logged in
            setShouldPoll(false);
        } else if (_isVideoRendering || videoStatus === VideoStatus.FAILED) {
            setShouldPoll(true);
        } else if (videoStatus === VideoStatus.PUBLISHED || videoStatus === VideoStatus.DRAFT) {
            setShouldPoll(false);
        }
    }, [shouldPoll, _isVideoRendering, videoStatus]);
};

/**
 * There are a variety of reasons we can't download the video (incorrect permissions, invalid video, etc)
 * We should call this function whenever we encounter an error downloading the video.
 */
export const handleDownloadErrors = (error: unknown, dispatch: Dispatch) => {
    if (error instanceof DownloadHttpError) {
        switch (error.status) {
            case HTTP_STATUS_CODES.NOT_FOUND:
                Toast.error(
                    'The video could not be found. Please confirm with the owner of the video and check the workspace settings to ensure you have access to the video.',
                );
                break;
            case HTTP_STATUS_CODES.FORBIDDEN:
                // since the share page is public, 403s on downloads are acceptable
                // do nothing
                break;
        }
    } else if (error instanceof DownloadLockedError) {
        switch (error.code) {
            case ErrorEnum.PREMIUM_MEDIA_CREDITS:
            case ErrorEnum.UPGRADE_FOR_UNLIMITED_MEDIA:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.UNLIMITED_PREMIUM_MEDIA,
                        },
                    }),
                );
                break;
            case ErrorEnum.HIT_MEDIA_CREDIT_LIMIT:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.HIT_MEDIA_CREDIT_LIMIT,
                        },
                    }),
                );
                break;
            case ErrorEnum.UPGRADE_FOR_AI_AVATAR:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.CANT_PUBLISH_AI_AVATAR,
                        },
                    }),
                );
                break;
            case ErrorEnum.VIDEO_DURATION_TOO_LONG:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.MAX_VIDEO_DURATION_LIMIT,
                        },
                    }),
                );
                break;
            case ErrorEnum.HIT_AI_VO_MINUTES_LIMIT:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.AI_VOICEOVER_MINUTES_LIMIT,
                        },
                    }),
                );
                break;
            case ErrorEnum.SUBSCRIPTION_PAUSED:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.SUBSCRIPTION_PAUSED,
                        },
                    }),
                );
                break;
            case ErrorEnum.HIT_RENDER_LIMIT:
                dispatch(
                    showModal({
                        modal_id: MODAL_IDS.FEATURE_LOCK,
                        modal_context: {
                            featureName: LOCKED_FEATURES.RENDER_LIMIT,
                        },
                    }),
                );
                break;
            default:
                // we haven't handled – probably missing an error type? FIX!
                Toast.error('There was an error downloading your video, please try again later');
                ErrorReporter.capture(error);
                break;
        }
    } else if (error instanceof UserPrintableError) {
        Toast.error(error.message);
    } else {
        Toast.error('There was an error downloading your video, please try again later');
    }
};

/**
 * This hook should initialize only 1 VideoDownloader on the share page. It waits for videoId to be populated in the store and
 * creates a VideoDownloader and save it in a ref. The hook returns the ref to the component, where it'll be used for the Download
 * button callback.
 */
export const useVideoDownloader = (
    videoId: number,
    setIsFetching: (isFetching: boolean) => void,
    setVideoDownloadUrl: (url: string) => void,
) => {
    const videoDownloader = useRef<VideoDownloader | null>(null);
    const dispatch = useAppDispatch();

    const downloadFinishedCallback = useCallback(
        (url: string) => {
            setIsFetching(false);
            setVideoDownloadUrl(getDownloadLink(url));
        },
        [setIsFetching, setVideoDownloadUrl],
    );
    const downloadFailedCallback = useCallback(
        (error: Error) => {
            handleDownloadErrors(error, dispatch);
        },
        [dispatch],
    );
    const queueVideoToRenderCallback = useCallback(() => {
        setIsFetching(true);
        dispatch(setVideoStatus(VideoStatus.QUEUED));
    }, [dispatch, setIsFetching]);
    useEffect(() => {
        // ensure video downloaded is created with a video id and only 1 is created
        if (videoId && !videoDownloader.current) {
            videoDownloader.current = new VideoDownloader({
                videoId,
                finishedCallback: downloadFinishedCallback,
                failCallback: downloadFailedCallback,
                renderingStartedCallback: queueVideoToRenderCallback,
            });
        }
    }, [downloadFailedCallback, downloadFinishedCallback, queueVideoToRenderCallback, videoId]);

    return videoDownloader;
};
